/**
 * Update: 15/04/2024 - Mariam Bawa - update import statements as part of data type refactoring 
 * Update: 18/04/2024 - Mariam Bawa - Cast ELS Income attribute response to work with updated LookupsState
 */
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { getIncomeAttributesBySnapshotId, putIncomeAttributesBySnapshotId } from '../../../redux/actions/incomeAttributes/incomeAttributes';
import { getLookups } from '../../../redux/actions/lookups/lookups';
import { IncomeAttributeState } from '../../../redux/reducers/getIncomeAttributes/getIncomeAttributesReducer';
import { ProfileOverviewState } from '../../../redux/reducers/getProfile/getProfileOverviewReducer';
import { SaveDataState } from '../../../redux/reducers/getSaveData/saveDataReducer';
import CapitalCostTab from './CapitalCostTab';
import { SaveResultState } from '../../../redux/reducers/getSaveData/saveResultReducer';
import { ControlledAccordion, useAccordionProvider } from '@szhsin/react-accordion';
import AccordionItem, { DefaultAccordionProviderOptions } from '../../../components/common/AccordionItem/AccordionItem';
import Total from '../../../components/common/Total/Total';
import calculateIncomeAttributesTotal from '../../../utils/calculateIncomeAttributesTotal';
import IncomeAttributeLookupObject from '../../../types/lookups/IncomeAttributeLookupObject';
import "../PIE.css";
import { useFormErrorContext } from '../../../utils/context/FormErrorContext';
import ErrorMessages from '../../../components/common/ErrorMessages/ErrorMessages';
import { useNavigation } from '../../../utils/context/NavigationContext';
import { selectGetIncomeAttributes, selectLookups, selectMenu, selectPreviousIncomeAttributes, selectProfileGetOverview, selectSaveData, selectSaveResult } from '../../../redux/selectors/exportSelectorPaths';
import PreviousCampaignIncomeAttributes from '../../../components/common/PreviousCampaignIncomeAttributes/PreviousCampaignIncomeAttributes';
import { accordionDisplay, getCurrentCampaignYear, getLastYearSubmittedCampaign, shouldDisplayPreviousTwoYearAttributes, shouldDisplayPreviousYearAttributes } from '../../../utils/previousIncomeAttributes';
import { MANAGE_PREVIOUS_INCOME_ATTRIBUTES_BY_SNAPSHOT_ID } from '../../../redux/actionTypes/actionsTypes';

export default function RevenueContainer(): JSX.Element {
  const { t } = useTranslation(['navigation', 'pie']);
  const dispatch = useDispatch();
  const providerValue = useAccordionProvider(DefaultAccordionProviderOptions)
  const profileOverview: ProfileOverviewState = useSelector(selectProfileGetOverview);
  const incomeAttributesState: IncomeAttributeState = useSelector(selectGetIncomeAttributes);
  const previousIncomeAttributesState: IncomeAttributeState = useSelector(selectPreviousIncomeAttributes);

  const menuState = useSelector(selectMenu);
  const tabList = menuState.activeItem.subItems;

  const lookupType = profileOverview.profile.profile_type_code.toLowerCase() + "-income-attributes";
  const lookupState = useSelector(selectLookups);
  const saveData: SaveDataState = useSelector(selectSaveData);
  const saveResultState: SaveResultState = useSelector(selectSaveResult);
  const [total, setTotal] = useState<number | undefined>(undefined)
  const { clearErrors, errors } = useFormErrorContext()
  const { direction } = useNavigation()
  const isInternal = (sessionStorage.getItem("isInternalUser") as string) === "true"
  const currentCampaignYear = getCurrentCampaignYear()

  const findAllPreviousAttributes = (year: string) => {
    const sessionStoragePreviousSnapshotData = sessionStorage.getItem(isInternal ? "previousSnapshotDataInternal" + year : "previousSnapshotData")
    if (sessionStoragePreviousSnapshotData !== null && sessionStoragePreviousSnapshotData !== undefined) {
      let previousSnapshotData = { incomeAttribute: [] }
      previousSnapshotData = JSON.parse(sessionStoragePreviousSnapshotData)
      if (previousSnapshotData.incomeAttribute) {
        return previousSnapshotData.incomeAttribute;
      }
    }
    return [];
  }

  const calcTotal = (year: string, previousYearTotal: boolean) => {
    const lookups = [...lookupState.lookups[3]["GLF.course"], ...lookupState.lookups[3]["GLF.building"]]
    const total = calculateIncomeAttributesTotal(lookups as IncomeAttributeLookupObject[],
      previousYearTotal ? findAllPreviousAttributes(year) : incomeAttributesState.data as Record<string, unknown>[])
    if (previousYearTotal) {
      return total === 0 ? undefined : total
    } else {
      setTotal(total)
      return undefined
    }
  }

  const getIncomeAttributes = () => {
    if ((!incomeAttributesState.loading && !incomeAttributesState.success)
      || incomeAttributesState.snapshotId !== Number(sessionStorage.getItem('currentSnapshotId') as string)) {
      dispatch(getIncomeAttributesBySnapshotId(sessionStorage.getItem('currentSnapshotId') as string));
    }

    const campaign = getLastYearSubmittedCampaign(profileOverview.profile)

    if (campaign && ((!previousIncomeAttributesState.loading && !previousIncomeAttributesState.success) || previousIncomeAttributesState.snapshotId !== campaign.currentSnapshotId)) {
      dispatch(getIncomeAttributesBySnapshotId(campaign.currentSnapshotId, MANAGE_PREVIOUS_INCOME_ATTRIBUTES_BY_SNAPSHOT_ID))
    }
  }


  useEffect(() => {
    getIncomeAttributes()
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (!incomeAttributesState.loading && incomeAttributesState.success
      && !lookupState.loading && lookupState.success
      && lookupState.lookups[3]) {
      calcTotal(String(currentCampaignYear), false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [incomeAttributesState, lookupState])

  useEffect(() => {
    if (saveData.saving) {
      clearErrors()
      const triggerSpectral = isInternal || direction !== "backward"


      const incomeAttributes = (incomeAttributesState.data as Record<string, unknown>[]).filter((data) => data.value !== null || data.description !== null || data.percent !== null);
      dispatch(putIncomeAttributesBySnapshotId(Number(sessionStorage.getItem("currentSnapshotId") as string), {
        "attributes": incomeAttributes,
        "spectral_section": profileOverview.profile.profile_type_code.toLowerCase() + "-" + menuState.activeItem.name,
        "trigger_spectral": triggerSpectral,
        "is_internal": isInternal
      }));
    }
  }, [dispatch, incomeAttributesState.data, menuState.activeItem.name, saveData.saving, profileOverview.profile.profile_type_code, clearErrors, direction, isInternal])

  useEffect(() => {
    if (saveResultState.status === 200) {
      dispatch(getIncomeAttributesBySnapshotId(Number(sessionStorage.getItem("currentSnapshotId") as string)));
    }
  }, [dispatch, saveResultState]);

  useEffect(() => {
    if (!lookupState.success) {
      dispatch(getLookups(lookupType));
    }
  }, [dispatch, lookupState.success, lookupType]);


  const findAttributeData = (attributeCode: string) => {
    let incomeAttributesData: Record<string, unknown> | undefined = undefined;
    if (incomeAttributesState.data) {
      incomeAttributesData = (incomeAttributesState.data as Record<string, unknown>[]).find(({ code }) => code === attributeCode);
    }
    return incomeAttributesData;
  }

  const handleCurrencyChange = (newAmt: number | undefined | null, name: string, _oldAmt: number | undefined | null) => {
    const idx = name.lastIndexOf("-");
    if (idx !== -1) {
      const code = name.substring(idx + 1);
      const incomeAttributesData = findAttributeData(code);
      const na = (newAmt === undefined || newAmt === null) ? null : newAmt
      if (incomeAttributesData) {
        incomeAttributesData["value"] = na;
      } else {
        (incomeAttributesState.data as Record<string, unknown>[]).push({
          snapshotId: Number(sessionStorage.getItem("currentSnapshotId") as string),
          code: code,
          description: null,
          value: na,
          type: "3",
          percent: null,
          year: null,
        });
      }
      calcTotal(String(currentCampaignYear), false)
    }
  }

  const handleCommentChange = (value: string, name: string) => {
    const idx = name.lastIndexOf("-");
    if (idx !== -1) {
      const code = name.substring(idx + 1);
      const incomeAttributesData = findAttributeData(code);
      const v = value.trim() === "" ? null : value.trim();
      if (incomeAttributesData) {
        incomeAttributesData["description"] = v;
      } else {
        (incomeAttributesState.data as Record<string, unknown>[]).push({
          snapshotId: Number(sessionStorage.getItem("currentSnapshotId") as string),
          code: code,
          description: v,
          value: null,
          type: "3",
          percent: null,
          year: null
        });
      }
    }
  }

  return (
    <div className='full-width'>
      <div className='content-container'>
        <ErrorMessages errors={errors} />
        {
          (menuState.activeItem.hasTabs && tabList) && (
            <>
              <div className='accordion-root'>
                <div className={accordionDisplay(isInternal, "accordion")}>
                  <ControlledAccordion providerValue={providerValue}>
                    {tabList.map(tab => {
                      return <AccordionItem header={t(tab.name, { ns: 'navigation' })}
                        key={tab.name}
                        itemKey={tab.name}
                      >
                        <CapitalCostTab
                          name={tab.name}
                          valueChangeHandler={handleCurrencyChange}
                          commentChangeHandler={handleCommentChange}
                          lookups={lookupState.lookups[3] ? (lookupState.lookups[3] as Record<string, IncomeAttributeLookupObject[]>)[tab.name] : []}
                          incomeAttributes={(incomeAttributesState.data && (incomeAttributesState.data as Record<string, unknown>[]).length > 0) ? (incomeAttributesState.data as Record<string, unknown>[]) : undefined}
                        />
                      </AccordionItem>
                    })}
                  </ControlledAccordion>
                </div>
                {shouldDisplayPreviousYearAttributes() ? <PreviousCampaignIncomeAttributes name={menuState.activeItem.name} tabComponent='capitalTabs' tabList={tabList} lookupType={lookupType} year={String(getCurrentCampaignYear() - 1)} /> : <></>}
                {isInternal && shouldDisplayPreviousTwoYearAttributes() ? <PreviousCampaignIncomeAttributes name={menuState.activeItem.name} tabComponent='capitalTabs' tabList={tabList} lookupType={lookupType} year={String(getCurrentCampaignYear() - 2)} /> : <></>}
              </div>
            </>
          )
        }
      </div>

      <div className='total-container'>
        <div className="accordion-root">
          <div className={accordionDisplay(isInternal, "accordion")}>
            <Total text={t('costs.total', { ns: 'pie' })} value={total} narrow={(shouldDisplayPreviousYearAttributes() || shouldDisplayPreviousTwoYearAttributes()) ? true : false} />
          </div>
          {shouldDisplayPreviousYearAttributes() ? <div className={accordionDisplay(isInternal, "right")}>
            <Total text={t('costs.total', { ns: 'pie' })} value={calcTotal(String(currentCampaignYear - 1), true)} />
          </div> : <></>}
          {isInternal && shouldDisplayPreviousTwoYearAttributes() ? <div className={accordionDisplay(isInternal, "right")}>
            <Total text={t('costs.total', { ns: 'pie' })} value={calcTotal(String(currentCampaignYear - 2), true)} />
          </div> : <></>}
        </div>
      </div>

    </div>);
}